import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import { PageContainer, Section } from '../../../components/AdminContentWrapper';
import { ADMIN_COLORS, containedButtonSx, darkFieldSx, outlinedButtonSx } from '../../../components/adminStyles';

const toText = (value) => (value === null || value === undefined ? '' : String(value));
const toNumberOrNull = (value) => (String(value).trim() === '' ? null : Number(value));

/**
 * Pantalla genérica de configuración de un modo de IA del chat ("prompts por
 * etapa"): Análisis Profundo, Generación de tableros. Cada página le pasa un
 * `descriptor`:
 *   { title, description, service: {getConfig, upsertConfig},
 *     stages: [{key, label, description, placeholders: [[name, meaning]], required: [...]}],
 *     limits: [{key, label, min, max}], options: [{key, label}],
 *     limitsTitle, limitsSubtitle, enabledLabel, disabledLabel, savedMessage }
 * Las reglas (`required`, rangos) replican las de analysis_config.py del
 * backend solo para avisar antes de guardar; el backend vuelve a validar.
 */
export const toForm = (config, descriptor) => ({
  enabled: config.enabled !== false,
  stages: Object.fromEntries(descriptor.stages.map(({ key }) => {
    const stage = (config.stages || {})[key] || {};
    return [key, {
      prompt_template: stage.prompt_template || '',
      model_id: toText(stage.model_id),
      max_tokens: toText(stage.max_tokens),
      temperature: toText(stage.temperature)
    }];
  })),
  limits: Object.fromEntries(descriptor.limits.map(({ key }) => [key, toText((config.limits || {})[key])])),
  options: Object.fromEntries((descriptor.options || []).map(({ key }) => [key, Boolean((config.options || {})[key])]))
});

export const toPayload = (form, descriptor) => ({
  enabled: form.enabled,
  stages: Object.fromEntries(descriptor.stages.map(({ key }) => {
    const stage = form.stages[key];
    return [key, {
      prompt_template: stage.prompt_template,
      model_id: stage.model_id.trim() || null,
      max_tokens: toNumberOrNull(stage.max_tokens),
      temperature: toNumberOrNull(stage.temperature)
    }];
  })),
  limits: Object.fromEntries(descriptor.limits.map(({ key }) => [key, toNumberOrNull(form.limits[key])])),
  ...((descriptor.options || []).length ? { options: { ...form.options } } : {})
});

export const missingRequirements = (descriptor, stageKey, prompt) => (
  descriptor.stages.find((stage) => stage.key === stageKey).required.filter((item) => !prompt.includes(item))
);

const monoFieldSx = {
  ...darkFieldSx,
  '& .MuiOutlinedInput-input': { fontFamily: 'monospace', fontSize: '0.85rem' }
};

const tabsSx = {
  borderBottom: `1px solid ${ADMIN_COLORS.border}`,
  mb: 2,
  '& .MuiTab-root': { color: ADMIN_COLORS.muted, textTransform: 'none', fontWeight: 600 },
  '& .MuiTab-root.Mui-selected': { color: ADMIN_COLORS.accent },
  '& .MuiTabs-indicator': { backgroundColor: ADMIN_COLORS.accent }
};

const placeholderChipSx = {
  fontFamily: 'monospace',
  backgroundColor: ADMIN_COLORS.selectedBg,
  color: ADMIN_COLORS.accent
};

const PromptPipelineSettings = ({ descriptor }) => {
  const { stages: STAGES, limits: LIMITS, options: OPTIONS = [], service } = descriptor;
  const [form, setForm] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [activeStage, setActiveStage] = useState(descriptor.stages[0].key);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await service.getConfig();
      setForm(toForm(data, descriptor));
      setDefaults(data.defaults || null);
      setMeta({ updated_at: data.updated_at || null, updated_by: data.updated_by || null });
    } catch (err) {
      setError(err.message || 'No fue posible obtener la configuración.');
    } finally {
      setIsLoading(false);
    }
  }, [service, descriptor]);

  useEffect(() => {
    load();
  }, [load]);

  const setStageField = (stageKey, field, value) => setForm((prev) => ({
    ...prev,
    stages: { ...prev.stages, [stageKey]: { ...prev.stages[stageKey], [field]: value } }
  }));

  const setLimit = (key, value) => setForm((prev) => ({ ...prev, limits: { ...prev.limits, [key]: value } }));

  const handleRestoreDefaults = () => {
    if (!defaults) return;
    setForm(toForm(defaults, descriptor));
    setSnackbar({ open: true, severity: 'warning', message: 'Se cargaron los valores por defecto. Revísalos y pulsa Guardar para aplicarlos.' });
  };

  const handleSave = async () => {
    const broken = STAGES
      .map(({ key, label }) => [label, missingRequirements(descriptor, key, form.stages[key].prompt_template)])
      .filter(([, missing]) => missing.length > 0);
    if (broken.length > 0) {
      const [label, missing] = broken[0];
      setActiveStage(STAGES.find((stage) => stage.label === label).key);
      setSnackbar({ open: true, severity: 'error', message: `Al prompt "${label}" le falta: ${missing.join(', ')}.` });
      return;
    }
    try {
      setIsSaving(true);
      await service.upsertConfig(toPayload(form, descriptor));
      setSnackbar({ open: true, severity: 'success', message: descriptor.savedMessage || 'Configuración guardada.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message || 'No se pudo guardar la configuración.' });
    } finally {
      setIsSaving(false);
    }
  };

  const stageInfo = STAGES.find((stage) => stage.key === activeStage);
  const stage = form?.stages[activeStage];
  const missing = stage ? missingRequirements(descriptor, activeStage, stage.prompt_template) : [];

  return (
    <PageContainer
      title={descriptor.title}
      description={descriptor.description}
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading} sx={outlinedButtonSx}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      {isLoading || !form ? (
        !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: ADMIN_COLORS.accent }} />
          </Box>
        )
      ) : (
        <>
          <Section
            title="Prompts por etapa"
            subtitle="Cada prompt debe conservar sus placeholders obligatorios y las etiquetas de su respuesta: el sistema lee el resultado del modelo por esas etiquetas."
          >
            <Tabs
              value={activeStage}
              onChange={(_, value) => setActiveStage(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={tabsSx}
            >
              {STAGES.map(({ key, label }) => (
                <Tab key={key} value={key} label={label} />
              ))}
            </Tabs>

            <Typography sx={{ color: ADMIN_COLORS.text, mb: 1.5 }}>{stageInfo.description}</Typography>

            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {stageInfo.placeholders.map(([name, meaning]) => (
                <Chip key={name} size="small" label={`${name}: ${meaning}`} sx={placeholderChipSx} />
              ))}
            </Stack>

            {missing.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Le falta: {missing.join(', ')}
              </Alert>
            )}

            <TextField
              value={stage.prompt_template}
              onChange={(event) => setStageField(activeStage, 'prompt_template', event.target.value)}
              multiline
              minRows={16}
              fullWidth
              inputProps={{ 'aria-label': `Prompt ${stageInfo.label}` }}
              sx={monoFieldSx}
            />

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="model_id (inference profile de Bedrock)"
                  placeholder="Vacío = modelo del agente"
                  value={stage.model_id}
                  onChange={(event) => setStageField(activeStage, 'model_id', event.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="max_tokens"
                  type="number"
                  placeholder="Del agente"
                  value={stage.max_tokens}
                  onChange={(event) => setStageField(activeStage, 'max_tokens', event.target.value)}
                  inputProps={{ min: 256, max: 32000, step: 1 }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={darkFieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="temperature"
                  type="number"
                  placeholder="Del agente"
                  value={stage.temperature}
                  onChange={(event) => setStageField(activeStage, 'temperature', event.target.value)}
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={darkFieldSx}
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            title={descriptor.limitsTitle || 'Límites'}
            subtitle={descriptor.limitsSubtitle}
          >
            <Grid container spacing={2}>
              {LIMITS.map(({ key, label, min, max }) => (
                <Grid item xs={12} sm={6} md={LIMITS.length > 4 ? 4 : 3} key={key}>
                  <TextField
                    label={label}
                    type="number"
                    value={form.limits[key]}
                    onChange={(event) => setLimit(key, event.target.value)}
                    inputProps={{ min, max, step: 1 }}
                    helperText={`Entre ${min} y ${max}`}
                    fullWidth
                    size="small"
                    sx={darkFieldSx}
                  />
                </Grid>
              ))}
            </Grid>

            <FormControlLabel
              sx={{ mt: 2, color: ADMIN_COLORS.text }}
              control={(
                <Switch
                  checked={form.enabled}
                  onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: ADMIN_COLORS.accent },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ADMIN_COLORS.accent }
                  }}
                />
              )}
              label={form.enabled ? descriptor.enabledLabel : descriptor.disabledLabel}
            />

            {OPTIONS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                sx={{ mt: 1, color: ADMIN_COLORS.text, display: 'flex' }}
                control={(
                  <Switch
                    checked={Boolean(form.options?.[key])}
                    onChange={(event) => setForm((prev) => ({ ...prev, options: { ...prev.options, [key]: event.target.checked } }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: ADMIN_COLORS.accent },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ADMIN_COLORS.accent }
                    }}
                  />
                )}
                label={label}
              />
            ))}

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Typography sx={{ color: ADMIN_COLORS.muted }}>
                {meta.updated_at
                  ? `Última actualización: ${meta.updated_at}${meta.updated_by ? ` por ${meta.updated_by}` : ''}`
                  : 'Todavía nadie guardó una configuración propia: se usan los valores por defecto de la plataforma.'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={handleRestoreDefaults}
                  disabled={!defaults || isSaving}
                  sx={{ ...outlinedButtonSx, whiteSpace: 'nowrap' }}
                >
                  Restaurar valores por defecto
                </Button>
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={containedButtonSx}
                >
                  {isSaving ? 'Guardando…' : 'Guardar'}
                </Button>
              </Stack>
            </Stack>
          </Section>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            color: 'var(--wb-on-primary)',
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

export default PromptPipelineSettings;
